import React from 'react';
import PropTypes from 'prop-types';

import cx from 'classnames';
import styles from './styles.scss';

export default class Slider extends React.Component {
  static PropTypes = {
    min: PropTypes.number,
    max: PropTypes.number,
    step: PropTypes.number,
    value: PropTypes.number,
    orientation: PropTypes.string,
    tooltip: PropTypes.bool,
    reverse: PropTypes.bool,
    format: PropTypes.func,
    onChange: PropTypes.func,
    onChangeComplete: PropTypes.func,
  };

  static defaultProps = {
    min: 0,
    max: 100,
    step: 1,
    value: 0,
    orientation: 'horizontal',
    tooltip: true,
    reverse: false,
  };

  constructor(props, context) {
    super(props, context);

    this.state = {
      limit: 0,
      grab: 0,
    };
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleUpdate);
    this.handleUpdate();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleUpdate);
  }

  /**
   * Prevent default event and bubbling
   * @param  {Object} e - Event object
   * @return {void}
   */
  handleNoop = e => {
    e.stopPropagation();
    e.preventDefault();
  };

  /**
   * Format label/tooltip value
   * @param  {Number} - value
   * @return {Formatted Number}
   */
  handleFormat = value => {
    const { format } = this.props;
    return format ? format(value) : value;
  };

  /**
   * Update slider state on change
   * @return {void}
   */
  handleUpdate = () => {
    const { orientation } = this.props;
    const dimension = capitalize(constants.orientation[orientation].dimension);
    const sliderPos = this.slider[`offset${dimension}`];
    const handlePos = this.handle[`offset${dimension}`];

    this.setState({
      limit: sliderPos - handlePos,
      grab: handlePos / 2,
    });
  };

  /**
   * Attach event listeners to mousemove/mouseup events
   * @return {void}
   */
  handleStart = () => {
    document.addEventListener('mousemove', this.handleDrag);
    document.addEventListener('mouseup', this.handleEnd);
  };

  /**
   * Handle drag/mousemove event
   * @param  {Object} e - Event object
   * @return {void}
   */
  handleDrag = e => {
    this.handleNoop(e);
    const { onChange } = this.props;
    const { target } = e;
    if (!onChange) return;

    let value = this.position(e);
    if (target.classList.contains(styles.label) && target.dataset.value) {
      value = parseFloat(target.dataset.value);
    }

    onChange && onChange(value, e);
  };

  /**
   * Detach event listeners to mousemove/mouseup events
   * @return {void}
   */
  handleEnd = e => {
    const { onChangeComplete } = this.props;
    onChangeComplete && onChangeComplete(e);
    document.removeEventListener('mousemove', this.handleDrag);
    document.removeEventListener('mouseup', this.handleEnd);
  };

  /**
   * Calculate position of slider based on its value
   * @param  {number} value - Current value of slider
   * @return {position} pos - Calculated position of slider based on value
   */
  getPositionFromValue = value => {
    const { limit } = this.state;
    const { min, max } = this.props;
    const diffMaxMin = max - min;
    const diffValMin = value - min;
    const percentage = diffValMin / diffMaxMin;
    return Math.round(percentage * limit);
  };

  /**
   * Translate position of slider to slider value
   * @param  {number} pos - Current position/coordinates of slider
   * @return {number} value - Slider value
   */
  getValueFromPosition = pos => {
    let value = null;
    const { limit } = this.state;
    const { orientation, min, max, step } = this.props;
    const percentage = clamp(pos, 0, limit) / (limit || 1);
    const baseVal = step * Math.round((percentage * (max - min)) / step);

    if (orientation === 'horizontal') {
      value = baseVal + min;
    } else {
      value = max - baseVal;
    }

    if (value >= max) value = max;
    if (value <= min) value = min;

    return value;
  };

  /**
   * Calculate position of slider based on value
   * @param  {Object} e - Event object
   * @return {number} value - Slider value
   */
  position = e => {
    const { grab } = this.state;
    const { orientation, reverse } = this.props;

    const node = this.slider;
    const coordinateStyle = constants.orientation[orientation].coordinate;
    const directionStyle = reverse
      ? constants.orientation[orientation].reverseDirection
      : constants.orientation[orientation].direction;
    const clientCoordinateStyle = `client${capitalize(coordinateStyle)}`;
    const coordinate = !e.touches ? e[clientCoordinateStyle] : e.touches[0][clientCoordinateStyle];
    const direction = node.getBoundingClientRect()[directionStyle];
    const pos = reverse ? direction - coordinate - grab : coordinate - direction - grab;
    return this.getValueFromPosition(pos);
  };

  /**
   * Grab coordinates of slider
   * @param  {Object} pos - Position object
   * @return {Object} - Slider fill/handle coordinates
   */
  coordinates = pos => {
    let fillPos = null;
    const { limit, grab } = this.state;
    const { orientation } = this.props;
    // const dimension = constants.orientation[orientation].dimension
    const value = this.getValueFromPosition(pos);
    const handlePos = this.getPositionFromValue(value);
    const sumHandleposGrab = orientation === 'horizontal' ? handlePos + grab : handlePos;

    if (orientation === 'horizontal') {
      fillPos = sumHandleposGrab;
    } else {
      fillPos = limit - sumHandleposGrab;
    }

    // if (this.handle && orientation === 'vertical') {
    //   labelPos = handlePos
    //   labelPos = handlePos - (this.handle.getBoundingClientRect()[dimension] * 0.75)
    // } else {
    //   labelPos = handlePos
    // }

    return {
      fill: fillPos,
      handle: handlePos,
      label: handlePos,
    };
  };

  render() {
    const { value, orientation, className, tooltip, reverse } = this.props;
    const dimension = constants.orientation[orientation].dimension;
    const direction = reverse
      ? constants.orientation[orientation].reverseDirection
      : constants.orientation[orientation].direction;
    const position = this.getPositionFromValue(value);
    const coords = this.coordinates(position);
    const fillStyle = { [dimension]: `${coords.fill}px` };
    const handleStyle = { [direction]: `${coords.handle}px` };

    return (
      <div
        ref={s => {
          this.slider = s;
        }}
        className={cx(styles.slider, styles[orientation], { [styles.reverse]: reverse }, className)}
        onMouseDown={this.handleDrag}
        onMouseUp={this.handleEnd}
        onTouchStart={this.handleDrag}
        onTouchEnd={this.handleEnd}>
        <div className={styles.fill} style={fillStyle} />
        <div
          ref={sh => {
            this.handle = sh;
          }}
          className={styles.handle}
          onMouseDown={this.handleStart}
          onTouchMove={this.handleDrag}
          onTouchEnd={this.handleEnd}
          style={handleStyle}>
          {tooltip && (
            <div
              ref={c => {
                this.tooltip = c;
              }}
              className={styles.tooltip}>
              <span>{this.handleFormat(value)}</span>
            </div>
          )}
        </div>
      </div>
    );
  }
}

/**
 * Capitalize first letter of string
 * @private
 * @param  {string} - String
 * @return {string} - String with first letter capitalized
 */
function capitalize(str) {
  return str.charAt(0).toUpperCase() + str.substr(1);
}

/**
 * Clamp position between a range
 * @param  {number} - Value to be clamped
 * @param  {number} - Minimum value in range
 * @param  {number} - Maximum value in range
 * @return {number} - Clamped value
 */
function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

const constants = {
  orientation: {
    horizontal: {
      dimension: 'width',
      direction: 'left',
      reverseDirection: 'right',
      coordinate: 'x',
    },
    vertical: {
      dimension: 'height',
      direction: 'top',
      reverseDirection: 'bottom',
      coordinate: 'y',
    },
  },
};



// WEBPACK FOOTER //
// ./app/components/slider/index.js